// engine-exception: board by status needs columns from the ticket_status catalog (a relation, not an enum) plus a queue switcher; the generic Board only derives columns from enum fields
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useResourceList, useUpdate } from '../../engine/api/hooks';
import { Board, type BoardColumn } from '../../engine/views/Board';
import { Badge, EmptyState, ErrorBanner, PageHeader, Skeleton } from '../../engine/design/primitives/Misc';
import { Chip } from '../../engine/design/primitives/Misc';
import { Switch } from '../../engine/design/primitives/Input';
import { Button } from '../../engine/design/primitives/Button';
import type { FieldMeta } from '../../engine/meta/types';
import { priorityTone, statusTone, usePriorities, useQueues, useStatuses, useTechnicians } from './lib/catalog';

// Tickets board: columns = the tenant's active statuses (in catalog order), cards = open
// tickets, dragging a card = POST-free PATCH of status_id (the API applies the SLA semantics).
// `?queue=<id>` narrows to one queue (the "queue board"); closed/cancelled columns are hidden
// unless toggled. Rows come from the same list endpoint as the table view.

const STATUS_FIELD: FieldMeta = { name: 'status_id', label: 'Status', type: 'relation', required: false, readOnly: false, nullable: true, filterable: true, sortable: true, searchable: false, inCreate: true, inUpdate: true, inList: true, inDetail: true };

export function TicketsBoardScreen() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const queueId = sp.get('queue');
  const [showClosed, setShowClosed] = useState(false);
  const statuses = useStatuses();
  const priorities = usePriorities();
  const queues = useQueues();
  const techs = useTechnicians();
  const update = useUpdate('tickets', { optimistic: true });

  const filter = useMemo(() => {
    const f: { field: string; op: 'eq' | 'is_null'; value: string | boolean }[] = [{ field: 'merged_into_ticket_id', op: 'is_null', value: true }];
    if (queueId) f.push({ field: 'queue_id', op: 'eq', value: queueId });
    return f;
  }, [queueId]);
  const list = useResourceList<Record<string, unknown>>('tickets', { filter, sort: '-created_at' }, { limit: 200 });

  const columns = useMemo<BoardColumn[]>(
    () => statuses.rows.filter((s) => s.is_active && (showClosed || (s.category !== 'closed' && s.category !== 'cancelled'))).map((s) => ({ value: s.id, label: s.name, tone: statusTone(s.category) })),
    [statuses.rows, showClosed],
  );
  const visible = useMemo(() => {
    const allowed = new Set(columns.map((c) => c.value));
    return list.rows.filter((r) => allowed.has(String(r['status_id'])));
  }, [list.rows, columns]);

  const setQueue = (id: string | null) => {
    const next = new URLSearchParams(sp);
    if (id) next.set('queue', id);
    else next.delete('queue');
    setSp(next, { replace: true });
  };

  if (statuses.error || list.error) return <ErrorBanner error={statuses.error ?? list.error} />;
  const loading = statuses.isLoading || list.isLoading;
  const queue = queues.rows.find((q) => q.id === queueId);

  return (
    <div className="hf-page">
      <PageHeader
        compact
        breadcrumb={[{ label: 'Tickets', to: '/tickets' }, { label: queue ? `${queue.name} board` : 'Board' }]}
        title={queue ? queue.name : 'Ticket board'}
        subtitle="Drag a card to change its status — SLA clocks follow the status."
        actions={
          <div className="hf-row">
            <Switch label="Show closed" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
            <Button variant="ghost" icon="table" onClick={() => navigate('/tickets')}>
              Table
            </Button>
          </div>
        }
      />
      <div className="hf-chiprow" role="tablist" aria-label="Queue">
        <Chip role="tab" aria-selected={!queueId} active={!queueId} onClick={() => setQueue(null)}>
          All queues
        </Chip>
        {queues.rows.map((q) => (
          <Chip key={q.id} role="tab" aria-selected={queueId === q.id} active={queueId === q.id} onClick={() => setQueue(q.id)} count={list.rows.filter((r) => r['queue_id'] === q.id).length}>
            {q.name}
          </Chip>
        ))}
      </div>
      {loading ? (
        <Skeleton lines={8} />
      ) : (
        <Board
          rows={visible}
          keyOf={(r) => String(r['id'])}
          field={STATUS_FIELD}
          columns={columns}
          labelOf={(r) => (
            <span className="hf-row">
              <span className="hf-mono hf-muted">{String(r['ref'] ?? '')}</span>
              <span className="hf-grow">{String(r['summary'] ?? '(no summary)')}</span>
            </span>
          )}
          card={(r) => {
            const p = priorities.byId.get(String(r['priority_id']));
            const t = techs.byId.get(String(r['assigned_tech_id']));
            return (
              <div className="hf-row" style={{ marginTop: 'var(--s-1)' }}>
                {p && <Badge tone={priorityTone(p.level)}>{p.name}</Badge>}
                <span className="hf-muted hf-grow" style={{ fontSize: 'var(--t-12)' }}>
                  {t ? t.name : 'Unassigned'}
                </span>
              </div>
            );
          }}
          onOpen={(r) => navigate(`/tickets/${String(r['id'])}`)}
          onMove={(row, value, previous) => update.mutate({ id: String(row['id']), body: { status_id: value }, previous: { status_id: previous } })}
          empty={<EmptyState title="No open tickets" hint={queue ? `Nothing in ${queue.name}.` : 'Nothing to work on.'} icon="kanban" />}
        />
      )}
    </div>
  );
}
